import { CheckOutlined, CloseOutlined, RollbackOutlined, SaveOutlined } from '@ant-design/icons'
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Switch,
} from 'antd'
import { FormInstance } from 'antd/lib/form'
import moment from 'moment'
import { isNil, pickBy } from 'ramda'
import React, { RefObject } from 'react'
import { connect } from 'react-redux'
import { firebaseConnect, firestoreConnect, isLoaded, withFirestore } from 'react-redux-firebase'
import { withRouter } from 'react-router-dom'
import { withHandlers, withState } from 'recompose'
import { compose } from 'redux'

import { SHOW_NOTIFICATION } from '../../../constants/ActionTypes'
import { DATE_FORMAT } from '../../../constants/dates'
import { ERROR, SUCCESS } from '../../../constants/notifications'
import {
  batchReprocessTournamentPlayers,
  fsCollections,
  makeInBatchWithFirebase,
} from '../../../helpers/firestore'
import { withHashedPin } from '../../../helpers/tournament'
import { ResultsOptionalColumn } from '../../../schemas/Tournament'
import { CountrySelect } from '../../countries'
import authorize from '../../higher-order/authorize'
import './TournamentForm.css'

// TODO unify with other forms
const inputRowLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
    lg: { span: 4 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
}

// TODO unify with other forms
const submitRowLayout = {
  wrapperCol: {
    xs: {
      span: 24,
      offset: 0,
    },
    sm: {
      span: 16,
      offset: 8,
    },
  },
}

const resultsOptionalColumns = [
  {
    value: ResultsOptionalColumn.Nationality,
    label: 'Nationality',
  },
  {
    value: ResultsOptionalColumn.Club,
    label: 'Club',
  },
  {
    value: ResultsOptionalColumn.CzechRegistrationId,
    label: 'ČMGS',
  },
]
class TournamentForm extends React.Component<any, any> {
  formRef: RefObject<FormInstance<any>> = React.createRef()

  handleSubmit = (values) => {
    const { onSubmit, hasFinalRound } = this.props

    const normalizeValues = (vs) => {
      const toPersist: FIXME = pickBy((value) => !isNil(value), {
        ...vs,
        multiday: null,
        date: null,
        dateSingle: null,
      })

      const { dateFromValue, dateToValue } = Array.isArray(values.date)
        ? { dateFromValue: values.date[0], dateToValue: values.date[1] }
        : { dateFromValue: values.dateSingle, dateToValue: values.dateSingle }

      return {
        ...toPersist,
        dateFrom: dateFromValue.format(DATE_FORMAT),
        dateTo: dateToValue.format(DATE_FORMAT),
        finalRound: hasFinalRound,
      }
    }

    onSubmit(normalizeValues(values))
  }

  handleFailure = ({ errorFields }) => {
    this.formRef?.current?.scrollToField(errorFields[0].name)
    /* TODO display somewhere */
    // tslint:disable-next-line:no-console
    console.log('Error during validation', errorFields)
  }

  render() {
    const {
      championships,
      tournamentId,
      tournament,
      previousLocation,
      goToPreviousLocation,
      isMultiday,
      setMultiday,
      hasFinalRound,
      setFinalRound,
    } = this.props
    const editing = location.pathname && location.pathname.includes('edit')

    const showDateRange =
      isMultiday === undefined
        ? tournament && tournament.dateFrom !== tournament.dateTo
        : isMultiday

    const form = (
      <div className="TournamentForm">
        <h3>{`${editing ? 'Edit' : 'Add'} Tournament`}</h3>
        <Form
          ref={this.formRef}
          onFinish={this.handleSubmit}
          onFinishFailed={this.handleFailure}
          layout="horizontal"
          initialValues={{
            ...tournament,
            date: tournament &&
              tournament.dateFrom &&
              tournament.dateTo && [moment(tournament.dateFrom), moment(tournament.dateTo)],
            dateSingle: tournament?.dateFrom && moment(tournament.dateFrom),

            teamsEnabled: tournament?.teamsEnabled || false,
            playersInFinalRound: tournament?.playersInFinalRound || 18,
            flightSize: tournament?.flightSize || 3,

            orderByDifferent: tournament?.orderByDifferent || false,

            public: tournament?.public || false,
            categoriesCloseAfterFinal: tournament?.categoriesCloseAfterFinal || false,
          }}>
          <Form.Item
            {...inputRowLayout}
            label="Name"
            hasFeedback
            name="name"
            rules={[{ required: true, message: 'Please input name!' }]}>
            <Input />
          </Form.Item>
          <Form.Item {...inputRowLayout} label="Short Name" hasFeedback name="shortName">
            <Input />
          </Form.Item>
          {showDateRange ? (
            <Form.Item {...inputRowLayout} label="Date" name="date" required hasFeedback>
              <DatePicker.RangePicker format="l" />
            </Form.Item>
          ) : (
            <Form.Item {...inputRowLayout} label="Date" name="dateSingle" required hasFeedback>
              <DatePicker format="l" />
            </Form.Item>
          )}
          <Form.Item {...inputRowLayout} label="Multiday">
            <div>
              <Checkbox
                checked={showDateRange}
                // tslint:disable-next-line:jsx-no-lambda
                onChange={(e) => setMultiday(e.target.checked)}
              />
            </div>
          </Form.Item>
          <Form.Item
            {...inputRowLayout}
            label="Venue"
            hasFeedback
            name="venue"
            rules={[{ required: true, message: 'Please input venue!' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            {...inputRowLayout}
            label="Country"
            hasFeedback
            name="country"
            rules={[{ required: true, message: 'Please select a country!' }]}>
            <CountrySelect showSelection />
          </Form.Item>

          <Divider />

          <Form.Item
            {...inputRowLayout}
            label="Rounds"
            hasFeedback
            name="rounds"
            rules={[
              { required: true, message: 'Please input number of rounds!' },
              { type: 'integer', message: 'Must be an integer!' },
            ]}>
            <InputNumber min={1} />
          </Form.Item>

          <Form.Item {...inputRowLayout} label="Final Round">
            <div>
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
                checked={hasFinalRound}
                // tslint:disable-next-line:jsx-no-lambda
                onChange={(e) => setFinalRound(e)}
              />
            </div>
          </Form.Item>

          <Form.Item
            {...inputRowLayout}
            label="Teams"
            hasFeedback
            name="teamsEnabled"
            valuePropName="checked"
            rules={[{ required: true, message: 'Please setup teams!' }]}>
            <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
          </Form.Item>

          {hasFinalRound && (
            <Form.Item
              {...inputRowLayout}
              label="Players in Final Round"
              hasFeedback
              name="playersInFinalRound"
              rules={[
                { required: true, message: 'Please input number of players in final round!' },
                { type: 'integer', message: 'Must be an integer!' },
              ]}>
              <InputNumber min={1} />
            </Form.Item>
          )}

          {hasFinalRound && (
            <Form.Item
              {...inputRowLayout}
              label="Categories Close After Final"
              hasFeedback
              name="categoriesCloseAfterFinal"
              valuePropName="checked"
              rules={[{ required: true, message: 'Set categories close after final flag!' }]}>
              <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
            </Form.Item>
          )}

          <Form.Item
            {...inputRowLayout}
            label="Flight Size"
            hasFeedback
            name="flightSize"
            rules={[
              { required: true, message: 'Please input flight size!' },
              { type: 'integer', message: 'Must be an integer!' },
            ]}>
            <InputNumber min={1} max={3} />
          </Form.Item>

          <Form.Item
            {...inputRowLayout}
            label="Different"
            hasFeedback
            name="orderByDifferent"
            valuePropName="checked"
            rules={[{ required: true, message: 'Set if of order is decided by different!' }]}>
            <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
          </Form.Item>

          <Form.Item
            {...inputRowLayout}
            label="PIN"
            hasFeedback
            name="correctLastHolePin"
            rules={[
              {
                required: isNil(tournament) || isNil(tournament.hashedPin),
                message: 'Please enter PIN for fixing last hole result!',
              },
              { pattern: /^[0-9]+$/, message: 'Only numbers are allowed in PIN' },
            ]}>
            <Input
              className="TournamentForm__pin"
              placeholder={tournament && tournament.hashedPin ? '●●●●' : undefined}
            />
          </Form.Item>

          <Divider />

          <Form.Item
            {...inputRowLayout}
            label="Screen Results Optional Columns"
            name="screenResultsOptionalColumns">
            <Checkbox.Group options={resultsOptionalColumns} />
          </Form.Item>

          <Form.Item
            {...inputRowLayout}
            label="Print Results Optional Columns"
            name="printResultsOptionalColumns">
            <Checkbox.Group options={resultsOptionalColumns} />
          </Form.Item>

          <Divider />

          <Form.Item {...inputRowLayout} label="Notes" name="notes">
            <Input.TextArea autoSize={{ minRows: 2 }} />
          </Form.Item>

          <Form.Item
            {...inputRowLayout}
            label="Public"
            hasFeedback
            name="public"
            valuePropName="checked"
            rules={[{ required: true, message: 'Set public flag!' }]}>
            <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
          </Form.Item>

          <Form.Item {...inputRowLayout} label="Championship" hasFeedback name="championshipId">
            <Select allowClear>
              {(championships || []).map((championship: FIXME) => (
                <Select.Option key={championship.id} value={championship.id}>
                  {championship.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item {...submitRowLayout}>
            <Button.Group>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                Submit
              </Button>
              {previousLocation ? (
                <Button danger onClick={goToPreviousLocation} icon={<RollbackOutlined />}>
                  Cancel
                </Button>
              ) : null}
            </Button.Group>
          </Form.Item>
        </Form>
        {editing && tournament?.championshipId && (
          <div style={{ textAlign: 'center' }}>
            <h3>Championship</h3>
            <Button
              loading={this.props.publishingResults}
              onClick={this.props.publishChampionshipResults}>
              Publish results
            </Button>
          </div>
        )}
      </div>
    )
    // tslint:enable

    return !tournamentId ||
      isLoaded(tournament, tournament?.championshipId ? championships : []) ? (
      form
    ) : (
      <Spin>{form}</Spin>
    )
  }
}

export default compose<FIXME>(
  authorize('admin'),
  firebaseConnect(),
  withFirestore,
  withRouter,
  firestoreConnect<FIXME>(({ tenant, tournamentId }) => [
    { collection: fsCollections.tournaments(tenant), doc: tournamentId },
    { collection: fsCollections.globalPlayers(tenant) },
    { collection: fsCollections.tournamentCategories(tenant, tournamentId) },
    { collection: fsCollections.tournamentPlayers(tenant, tournamentId) },
    { collection: fsCollections.championships(tenant) }, // TODO filter active
  ]),
  connect(({ firestore }: FIXME, { tenant, tournamentId }: FIXME) => ({
    globalPlayers: firestore.ordered[fsCollections.globalPlayers(tenant)],
    tournament: (firestore.data[fsCollections.tournaments(tenant)] || {})[tournamentId],
    tournamentCategories:
      firestore.ordered[fsCollections.tournamentCategories(tenant, tournamentId)],
    tournamentPlayers: firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
    championships: firestore.ordered[fsCollections.championships(tenant)],
  })),
  withState('publishingResults', 'setPublishingResults', false),
  withHandlers<FIXME, FIXME>({
    goToPreviousLocation: ({ history, previousLocation }) => () => {
      if (previousLocation) {
        history.push(previousLocation)
      }
    },
    inBatchBound: makeInBatchWithFirebase,
    publishChampionshipResults: ({
      dispatch,
      firebase,
      tenant,
      tournamentId,
      setPublishingResults,
    }) => () => {
      setPublishingResults(true)
      firebase
        .functions()
        .httpsCallable('updateChampionshipResultsFromTournament')({ tenant, tournamentId })
        // tslint:disable-next-line:no-console
        .then((result) => {
          setPublishingResults(false)
          dispatch({
            type: SHOW_NOTIFICATION,
            message: 'Results published to championship',
            kind: SUCCESS,
          })
        })
        .catch((err) => {
          setPublishingResults(false)
          // tslint:disable-next-line:no-console
          console.error('Failed to publish results to championship', err)
          dispatch({
            type: SHOW_NOTIFICATION,
            message: 'Failed to publish results to championship',
            kind: ERROR,
          })
        })
    },
  }),
  withHandlers({
    onSubmitSuccess: ({ dispatch, goToPreviousLocation }) => () => {
      dispatch({ type: SHOW_NOTIFICATION, message: 'Tournament saved', kind: SUCCESS })
      goToPreviousLocation()
    },
    onSubmitError: ({ dispatch }) => (err) => {
      // tslint:disable-next-line:no-console
      console.error('Failed to save tournament', err)
      dispatch({ type: SHOW_NOTIFICATION, message: 'Failed to save tournament', kind: ERROR })
    },
    insertTournament: ({ firestore, tenant }) => (tournament) =>
      firestore.add({ collection: fsCollections.tournaments(tenant) }, tournament),
    updateTournament: ({
      inBatchBound,
      tenant,
      tournamentId,
      globalPlayers,
      tournamentCategories,
      tournamentPlayers,
    }) => (tournament) =>
      inBatchBound((firestore, batch) => {
        const collection = firestore.collection(fsCollections.tournaments(tenant))
        batch.set(collection.doc(tournamentId), tournament)

        // TODO trigger only when dateFrom is changed
        batchReprocessTournamentPlayers({
          tenant,
          tournamentId,
          tournament,
          globalPlayers,
          tournamentCategories,
          tournamentPlayers,
        })(firestore, batch)
      }),
  }),
  withHandlers({
    upsertTournament: ({ insertTournament, updateTournament, tournamentId, tournament }) => (
      tournamentFields,
    ) => {
      const { correctLastHolePin, ...remainingFields } = tournamentFields

      const updatedTournament = isNil(correctLastHolePin)
        ? { ...remainingFields, hashedPin: tournament.hashedPin }
        : withHashedPin(remainingFields, correctLastHolePin)

      return tournamentId
        ? updateTournament(updatedTournament)
        : insertTournament(updatedTournament)
    },
  }),
  withHandlers({
    onSubmit: ({ upsertTournament, onSubmitSuccess, onSubmitError }) => (tournament) =>
      upsertTournament(tournament).then(onSubmitSuccess).catch(onSubmitError),
  }),
  withState('isMultiday', 'setMultiday', undefined),
  withState('hasFinalRound', 'setFinalRound', ({ tournament }) => tournament?.finalRound || false),
)(TournamentForm)



// WEBPACK FOOTER //
// ./src/components/tournament/management/TournamentForm.tsx